import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import useNavigation from "Hooks/navigation.hook";
import {
  authPassportAtom,
  isAuthAtom,
  authActionStatusAtom,
} from "Recoil/auth.atom";
import { authLogout } from "Services/auth.services";
import { contentHeaderAtom, breadcrumbsAtom } from "Recoil/header.atom";
import {
  Route,
  Redirect,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { isArray, isNil, map, isEmpty, first } from "lodash";
import { FrownOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar as AntAvatar, Result, Space } from "antd";
import {
  ActionMenu,
  Avatar,
  Notifications,
  Layout,
} from "@life-line-lab/components-react";
import rawNavigation from "./navigation";
import "@life-line-lab/components-react/build/index.css";
import "./styles/base.scss";
import GlobalFilter from "Components/shared/global-filter";
import { useSession } from "Hooks/index";
import { AccountRole } from "Types/common.types";

const Protected = ({ children }) => {
  const isAuth = useRecoilValue<boolean>(isAuthAtom);

  if (!isAuth) {
    // Remove sidebar activeKey
    localStorage.removeItem("sb_activekey");
  }

  return isAuth ? (
    <Route children={children} />
  ) : (
    <Redirect to={{ pathname: "/login" }} />
  );
};

const HeaderBody = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { org } = useSession();

  const [status, setStatus] = useRecoilState<any>(authActionStatusAtom);
  const [orgType, setOrgType] = useState<AccountRole>();
  const authPassport = useRecoilValue<any>(authPassportAtom);
  const profile = authPassport.profile;

  useEffect(() => {
    if (!isEmpty(org)) {
      setOrgType(org.orgType);
    }
  }, [org]);

  const handleMenuClick = async (action: string) => {
    switch (action) {
      case "logout":
        setStatus({ ...status, logout: "loading" });
        const responseData = await authLogout();

        if (!isNil(responseData)) {
          setStatus({ ...status, logout: "login" });
          history.push("/login");
        } else {
          setStatus({ ...status, logout: "error" });
        }
        break;

      case "profile":
        history.push("/settings/personal-info");
        break;
      case "changepwd":
        history.push("/settings/change-password");
        break;
      default:
        break;
    }
  };

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);
    localStorage.setItem("i18next", value);
    history.go(0);
  };

  const filterList = [
    { id: "all", name: "All" },
    { id: "1", name: "Diolko Logistics", color: "red" },
    { id: "2", name: "Ninjavan Logistics", color: "darkgray" },
    {
      id: "3",
      name: "Skynet Logistics",
      color: "purple",
      children: [
        { id: "31", name: "Skynet Malaysia", color: "orange" },
        { id: "32", name: "Skynet Thailand", color: "blue" },
        { id: "33", name: "Skynet Singapore", color: "yellow" },
      ],
    },
  ];

  return (
    <React.Fragment>
      <div className="header--body-right">
        <Space direction="horizontal" size="large">
          {/* <LanguageSelector
            mode="long"
            currentLanguage={i18n.language}
            options={[
              {
                label: 'English',
                initial: 'EN',
                value: 'en',
              },
              {
                label: 'Bahasa Melayu',
                initial: 'BM',
                value: 'ms',
              }
            ]}
            onChange={handleLanguageChange}
          /> */}

          <Notifications header="Notifications" notifications={[]} footer="" />

          {/*<ActionMenu
            className="header--action-menu"
            label={<AntAvatar icon={<UserOutlined />} />}
            header={
              <Space direction="horizontal" size="small">
                <Avatar icon={<UserOutlined />} size="small" />
                {profile.fullName}
              </Space>
            }
            actions={[
              {
                label: t('nav.profile'),
                onClick: () => handleMenuClick('profile')
              },
              {
                label: t('nav.changePassword'),
                onClick: () => handleMenuClick('changepwd')
              },
              {
                label: t('nav.logout'),
                onClick: () => handleMenuClick('logout')
              }
            ]}
          />*/}

          <ActionMenu
            className="header--action-menu"
            label={<AntAvatar icon={<UserOutlined />} />}
            header={
              <Space direction="horizontal" size="small">
                <Avatar icon={<UserOutlined />} size="small" />
                {profile.fullName}
              </Space>
            }
            actions={[
              {
                label: t("nav.profile"),
                onClick: () => handleMenuClick("profile"),
              },
              {
                label: t("nav.changePassword"),
                onClick: () => handleMenuClick("changepwd"),
              },
              {
                label: t("nav.logout"),
                onClick: () => handleMenuClick("logout"),
              },
            ]}
          />
          <div
            style={{
              padding: "10px",
              paddingLeft: "25px",
              borderLeft: "1px solid lightgray",
            }}
          >
            {profile.fullName} <br />
            <span style={{ fontSize: "12px", color: "grey" }}>{org.name}</span>
          </div>
          {(orgType === AccountRole.OWNER ||
            orgType === AccountRole.LOGISTIC_PROVIDER) && <GlobalFilter />}
        </Space>
      </div>
    </React.Fragment>
  );
};

const NoMatch = () => {
  const { t } = useTranslation();
  return (
    <div>
      <Result
        icon={<FrownOutlined />}
        title={t("txt.pageNotFound")}
        subTitle={t("txt.pageNotFoundOrNoAccess")}
      />
    </div>
  );
};

const HOME_PATH = "/delivery-management/parcel-tracking";

const getRoleTypeDefaults = (roleTypes) => {
  // if nothing found, return default
  if (!roleTypes) return HOME_PATH;

  return HOME_PATH;
};

interface ShellProps {
  routes: any;
}

const Shell: React.FC<ShellProps> = ({ routes }) => {
  const logo = `/assets/images/banner_logo.png`;
  const location = useLocation();

  const contentHeader = useRecoilValue(contentHeaderAtom);
  const breadcrumbs = useRecoilValue(breadcrumbsAtom);

  const authPassport = useRecoilValue<any>(authPassportAtom);
  const roleTypes = authPassport.roleTypes;
  const navigation = useNavigation({ rawNavigation, roles: roleTypes });

  const currentHref = `${location.pathname}${location.search}`;

  return (
    <Switch>
      {isArray(routes.public) &&
        map(routes.public, (route, index) => {
          if (route.isActive) {
            return (
              <Route
                key={index}
                path={route.path}
                strict={route.strict}
                exact={route.exact}
                render={() => route.component}
              />
            );
          }
        })}

      {isArray(routes.protected) && (
        <Protected>
          <Layout
            headerProps={{
              logoSrc: logo,
              logoSize: "small",
              logoHref: "/",
            }}
            headerChildren={<HeaderBody />}
            contentHeaderChildren={contentHeader}
            hasSidebar
            navigation={navigation}
            currentHref={currentHref}
          >
            <Switch>
              {map(routes.protected, (route, index) => {
                if (route.isActive) {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      strict={route.strict}
                      exact={route.exact}
                      render={() => route.component}
                    />
                  );
                }

                try {
                  if (
                    typeof route?.isActiveFunction === "function" &&
                    route.isActiveFunction(roleTypes)
                  ) {
                    return (
                      <Route
                        key={index}
                        path={route.path}
                        strict={route.strict}
                        exact={route.exact}
                        render={() => route.component}
                      />
                    );
                  }
                } catch (error) {
                  console.error("An error occurred in mapping routes", error);
                }
              })}

              <Route
                exact
                path="/"
                render={() => <Redirect to={getRoleTypeDefaults(roleTypes)} />}
              />
              <Route path="*">
                <NoMatch />
              </Route>
            </Switch>
          </Layout>
        </Protected>
      )}
    </Switch>
  );
};

export default Shell;
